import React from 'react';
import { Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { BuildingsIcon } from '../../img/BuildingsIcon';
import pdfComputerImage from '../../img/pdf-computer.png';
import zillowRentalsLogo from '../../img/ZillowRentals-brand-light.png';

type PdfCoverPageProps = {
  title: string;
  propertyName: string | null;
  propertyImageUrl?: string;
  date: string;
  filters: Array<{ label: string; value: string }>;
};

const BLUE_BG = 'rgb(0, 65, 217)';
const GREEN_ACCENT = 'rgb(115, 228, 249)';

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#FFFFFF',
    padding: 24,
  },
  blueContainer: {
    backgroundColor: BLUE_BG,
    padding: 24,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
  },
  topSection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    gap: 8,
  },
  portfolioTitle: {
    fontFamily: 'ObjectSans',
    fontSize: 60,
    color: '#FFFFFF',
  },
  propertyOverviewTitle: {
    fontFamily: 'ObjectSans',
    fontSize: 20,
    color: '#FFFFFF',
  },
  propertyName: {
    fontFamily: 'ObjectSans',
    fontSize: 36,
    color: '#FFFFFF',
    marginTop: 4,
  },
  date: {
    fontFamily: 'OpenSansBold',
    fontSize: 14,
    color: GREEN_ACCENT,
    marginTop: 8,
  },
  filtersContainer: {
    marginTop: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  filterRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  filterLabel: {
    fontFamily: 'OpenSansBold',
    fontSize: 11,
    color: '#FFFFFF',
    width: 100,
  },
  filterValue: {
    fontFamily: 'OpenSans',
    fontSize: 11,
    color: '#FFFFFF',
  },
  illustrationContainer: {
    position: 'absolute',
    bottom: 24,
    right: 48,
    width: 250,
    height: 250,
  },
  illustration: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  buildingsContainer: {
    position: 'absolute',
    bottom: 48,
    right: 60,
    width: 200,
    height: 200,
    backgroundColor: '#1A54DD',
    borderRadius: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoContainer: {
    backgroundColor: '#FFFFFF',
    borderTopRightRadius: 8,
    borderTopLeftRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    bottom: -28,
    alignSelf: 'flex-start',
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
});

const PortfolioCoverContent = ({
  title,
  date,
  filters,
}: {
  title: string;
  date: string;
  filters: PdfCoverPageProps['filters'];
}) => (
  <>
    <View style={styles.topSection}>
      <Text style={styles.portfolioTitle}>{title}</Text>
      <Text style={styles.date}>{date}</Text>
      {filters.length > 0 && (
        <View style={styles.filtersContainer}>
          {filters.map((filter) => (
            <View key={filter.label} style={styles.filterRow}>
              <Text style={styles.filterLabel}>{filter.label}</Text>
              <Text style={styles.filterValue}>{filter.value}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
    <View style={styles.illustrationContainer}>
      <Image style={styles.illustration} src={pdfComputerImage.src} />
    </View>
  </>
);

const PropertyCoverContent = ({
  title,
  propertyName,
  propertyImageUrl,
  date,
}: {
  title: string;
  propertyName: string;
  propertyImageUrl?: string;
  date: string;
}) => (
  <View style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
    <View style={styles.topSection}>
      <Text style={styles.propertyOverviewTitle}>{title}</Text>
      <Text style={styles.propertyName}>{propertyName}</Text>
      <Text style={styles.date}>{date}</Text>
    </View>
    <View style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {propertyImageUrl ? (
        <View
          style={{
            borderRadius: 12,
            overflow: 'hidden',
            width: '95%',
            height: '100%',
          }}
        >
          <Image src={propertyImageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </View>
      ) : (
        <View
          style={{
            width: 200,
            height: 200,
            backgroundColor: '#1A54DD',
            borderRadius: 16,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <View style={{ transform: 'scale(4)' }}>
            <BuildingsIcon />
          </View>
        </View>
      )}
    </View>
  </View>
);

export const PdfCoverPage = ({ title, propertyName, propertyImageUrl, date, filters }: PdfCoverPageProps) => {
  const isPortfolio = propertyName === null;

  return (
    <Page size="LEGAL" orientation="landscape" style={styles.page}>
      <View style={styles.blueContainer}>
        {isPortfolio ? (
          <PortfolioCoverContent title={title} date={date} filters={filters} />
        ) : (
          <PropertyCoverContent
            title={title}
            propertyName={propertyName}
            propertyImageUrl={propertyImageUrl}
            date={date}
          />
        )}
        <View style={styles.bottomSection}>
          <View style={styles.logoContainer}>
            <Image src={zillowRentalsLogo.src} style={{ width: 160, height: 19, objectFit: 'contain' }} />
          </View>
        </View>
      </View>
    </Page>
  );
};
